import { useEffect, useState, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Paper,
  ClickAwayListener,
  Checkbox,
} from "@mui/material";

import mainStyle from "./mainStyle.module.scss";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up.svg";
import { ReactComponent as ChevronUpHover } from "assets/images/icons/icn_pagination_chevron_up_hover.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down.svg";
import { ReactComponent as ChevronDownHover } from "assets/images/icons/icn_pagination_chevron_down_hover.svg";
import { ReactComponent as Search } from "assets/images/icons/icn_search.svg";
import { ReactComponent as SearchHover } from "assets/images/icons/icn_search_hover.svg";
import { ReactComponent as SelectIcon } from "assets/images/icons/icn_selected.svg";
import { ReactComponent as UnSelectIcon } from "assets/images/icons/icn_unselected.svg";

export const SearchWithSelectCheckbox = ({
  value = [],
  onChange,
  options = [],
  disabledOptions = [],
  placeholder = "Select",
  width = "200px",
}) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchHover, setSearchHover] = useState(false);
  const [chevronHover, setChevronHover] = useState(false);
  const [menuWidthPx, setMenuWidthPx] = useState(width);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const menuOpen = Boolean(anchorEl);

  const filteredOptions = options.filter(
    (opt) =>
      typeof opt.value === "string" &&
      opt.value.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (containerRef.current) setMenuWidthPx(containerRef.current.offsetWidth);
  }, [width]);

  const handleOpen = (event) => {
    event.stopPropagation();
    triggerRef.current = event.currentTarget;
    setAnchorEl(anchorEl ? null : event.currentTarget);
    if (!anchorEl) setSearch("");
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
    setChevronHover(false);
    if (triggerRef.current) triggerRef.current.focus();
  };

  const handleSearchClick = (event) => {
    event.stopPropagation();
  };

  const isAllOption = (opt) =>
    typeof opt.value === "string" && opt.value.toLowerCase().includes("all");

  const handleSelectOption = (opt) => {
    if (isAllOption(opt)) {
      if (value.length === options.length - 1) {
        onChange([]);
      } else {
        const allExceptAll = options
          .filter((o) => !isAllOption(o))
          .map((o) => o.value);
        onChange(allExceptAll);
      }
    } else {
      if (value.includes(opt.value)) {
        onChange(value.filter((v) => v !== opt.value));
      } else {
        onChange([...value, opt.value]);
      }
    }
  };

  const renderValue = () => {
    if (Array.isArray(value) && value.length > 0) {
      return value.join(", ");
    }
    return (
      <span style={{ color: theme.palette.primary.greyThird }}>
        {placeholder}
      </span>
    );
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleOpen}
      onMouseEnter={() => setChevronHover(true)}
      onMouseLeave={() => setChevronHover(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${
          menuOpen
            ? theme.palette.primary.main
            : chevronHover
            ? theme.palette.primary.mainHover
            : theme.palette.primary.borderGreyPrimary
        }`,
        borderRadius: theme.shape.customBorderRadius[1],
        width: width,
        height: 30,
        bgcolor: theme.palette.primary.white,
        position: "relative",
        px: 1,
        cursor: "pointer",
      }}
      tabIndex={0}
    >
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Typography
          sx={{
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.greyPrimary,
            fontWeight: theme.typography.customFontWeight[3],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "Open Sans",
          }}
        >
          {renderValue()}
        </Typography>
      </Box>

      {/* Chevron */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        {menuOpen ? (
          chevronHover ? (
            <ChevronUpHover />
          ) : (
            <ChevronUp />
          )
        ) : chevronHover ? (
          <ChevronDownHover />
        ) : (
          <ChevronDown />
        )}
      </Box>

      {/* Dropdown Menu */}
      {menuOpen && (
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              mt: 0.5,
              maxWidth: menuWidthPx,
              borderRadius: 3,
              zIndex: theme.zIndex.modal,
              boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
            }}
          >
            {/* Search */}
            <Box
              onMouseEnter={() => setSearchHover(true)}
              onMouseLeave={() => setSearchHover(false)}
              onClick={handleSearchClick}
              sx={{
                p: 1,
                bgcolor: theme.palette.primary.white,
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: theme.palette.primary.white,
                    "& fieldset": {
                      borderColor: theme.palette.primary.borderGreyPrimary,
                    },
                    "&:hover fieldset": {
                      borderColor: theme.palette.primary.mainHover,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.palette.primary.main,
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "7px 14px",
                      fontSize: theme.typography.customFontSize[2],
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchHover ? <SearchHover /> : <Search />}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Options */}
            <List
              className={mainStyle["custom-scrollbar-menu"]}
              sx={{
                paddingTop: "5px",
                paddingLeft: "10px",
                paddingRight: "10px",
                boxSizing: "content-box",
                marginRight: "10px",
                maxHeight: "180px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {filteredOptions.map((opt) => {
                const isAll = isAllOption(opt);
                const allSelected =
                  value.length ===
                  options.filter((o) => !isAllOption(o)).length;

                return (
                  <ListItem
                    key={opt.value}
                    button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOption(opt);
                    }}
                    disabled={disabledOptions.includes(opt.value)}
                    sx={{
                      color: theme.palette.primary.main,
                      paddingLeft: "10px",
                      margin: "5px 5px 0 0",
                      borderRadius: theme.shape.customBorderRadius[0],
                      height: "40px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.lightBlue,
                      },
                      backgroundColor:
                        isAll && allSelected
                          ? theme.palette.primary.subBlue
                          : "transparent",
                    }}
                  >
                    {!isAll && (
                      <Checkbox
                        checked={value.includes(opt.value)}
                        checkedIcon={<SelectIcon />}
                        icon={<UnSelectIcon />}
                        sx={{ mr: 1, p: 0 }}
                      />
                    )}
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        style: {
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          fontSize: theme.typography.customFontSize[2],
                        },
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};
